import { Route, Routes } from "react-router-dom";

import { AppProviders } from "@/providers";
import { DocumentTitle } from "@/components/document-title";
import { AppErrorBoundary } from "@/components/error-boundary";
import { ProtectedRoute, PublicAuthRoute, RequireRole } from "@/components/protected-route";

import { DashboardPage } from "@/pages/dashboard";
import { ShopsPage } from "@/pages/shops";
import { LoginPage } from "@/pages/login";
import { SignupPage } from "@/pages/signup";
import { CheckEmailPage } from "@/pages/check-email";
import { VerifyEmailPage } from "@/pages/verify-email";
import { EventsListPage as EventsPage } from "@/pages/events";
import { NewEventPage } from "@/pages/events/new";
import { EventDetailPage } from "@/pages/events/detail";
import { OrdersPage } from "@/pages/orders";
import { OrderDetailPage } from "@/pages/orders/detail";
import { PromotionsPage } from "@/pages/promotions";
import { NewPromotionPage } from "@/pages/promotions/new";
import { PromotionDetailPage } from "@/pages/promotions/detail";
import { SettingsPage } from "@/pages/settings";
import { LegalOverridesPage as LegalSettingsPage } from "@/pages/settings/legal";
import { NewShopPage } from "@/pages/shops/new";
import { ShopDetailPage } from "@/pages/shops/detail";
import { OnboardingPage } from "@/pages/onboarding";
import { TeamPage } from "@/pages/team";
import { AuditPage } from "@/pages/audit";
import { ReportsPage } from "@/pages/reports";
import { EventReportsPage } from "@/pages/event-reports";
import { EventReportDetailPage } from "@/pages/event-reports/detail";
import { AcceptInvitePage } from "@/pages/accept-invite";
import { ForgotPasswordPage } from "@/pages/forgot-password";
import { ResetPasswordPage } from "@/pages/reset-password";
import { CheckInPage } from "@/pages/check-in";
import { FinancePage } from "@/pages/finance";
import { ForbiddenPage } from "@/pages/forbidden";
import { NotFoundPage } from "@/pages/not-found";

export function App() {
  return (
    <AppProviders>
      <DocumentTitle />
      <AppErrorBoundary>
        <Routes>
          {/* Auth-redirect-exempt public sayfalar */}
          <Route element={<PublicAuthRoute allowAuthenticated />}>
            <Route path="/check-email" element={<CheckEmailPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/accept-invite" element={<AcceptInvitePage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
          </Route>

          {/* Auth sayfaları (login'liyse /'a redirect) */}
          <Route element={<PublicAuthRoute />}>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          </Route>

          {/* Auth gerektiren sayfalar */}
          <Route element={<ProtectedRoute />}>
            {/* Ana dashboard — SalesReport rolleri KPI/grafik görür; Editor parasız
                ana sayfaya düşer, Gate /check-in'e yönlenir. */}
            <Route path="/" element={<DashboardPage />} />
            <Route path="/forbidden" element={<ForbiddenPage />} />

            {/* Owner-only */}
            <Route element={<RequireRole roles={["Owner"]} />}>
              <Route path="/team" element={<TeamPage />} />
              <Route path="/finance" element={<FinancePage />} />
            </Route>

            {/* Mağaza listeleme — Editor & Accountant da yönetim ekranını görmeli. */}
            <Route element={<RequireRole roles={["Owner", "Manager", "Editor", "Accountant"]} />}>
              <Route path="/shops" element={<ShopsPage />} />
            </Route>

            {/* Owner | Manager (shop ayarları, ödeme yöntemleri, legal, settings) */}
            <Route element={<RequireRole roles={["Owner", "Manager"]} />}>
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="/shops/new" element={<NewShopPage />} />
              <Route path="/shops/:slug" element={<ShopDetailPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/settings/legal" element={<LegalSettingsPage />} />
              <Route path="/audit" element={<AuditPage />} />
            </Route>

            {/* Owner | Manager | Editor (event & promotion CRUD) */}
            <Route element={<RequireRole roles={["Owner", "Manager", "Editor"]} />}>
              <Route path="/events" element={<EventsPage />} />
              <Route path="/events/new" element={<NewEventPage />} />
              <Route path="/events/:slug" element={<EventDetailPage />} />
              <Route path="/promotions" element={<PromotionsPage />} />
              <Route path="/promotions/new" element={<NewPromotionPage />} />
              <Route path="/promotions/:id" element={<PromotionDetailPage />} />
            </Route>

            {/* Owner | Manager | Editor | Accountant (order list/detail, refund backend kontrol eder) */}
            <Route element={<RequireRole roles={["Owner", "Manager", "Editor", "Accountant"]} />}>
              <Route path="/orders" element={<OrdersPage />} />
              <Route path="/orders/:orderNumber" element={<OrderDetailPage />} />
            </Route>

            {/* Owner | Manager | Accountant (satış raporu + etkinlik sonu raporları). */}
            <Route element={<RequireRole roles={["Owner", "Manager", "Accountant"]} />}>
              <Route path="/reports" element={<ReportsPage />} />
              <Route path="/reports/events" element={<EventReportsPage />} />
              <Route path="/reports/events/:eventId" element={<EventReportDetailPage />} />
            </Route>

            {/* Check-in: Owner | Manager | Editor | Gate */}
            <Route element={<RequireRole roles={["Owner", "Manager", "Editor", "Gate"]} />}>
              <Route path="/check-in" element={<CheckInPage />} />
            </Route>
          </Route>

          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </AppErrorBoundary>
    </AppProviders>
  );
}
